import { useState, type ReactNode } from "react";
import {
  CalendarClock,
  CheckCircle2,
  ClipboardCheck,
  Download,
  FileClock,
  FileText,
  Scale,
} from "lucide-react";
import { useNavigate } from "react-router";
import CleButton from "@cle/components/CleButton";
import CleTable, { type CleTableColumn } from "@cle/components/CleTable";
import RoundedCard from "@cle/components/RoundedCard";
import {
  useDownloadAfgorelseDocument,
  useReportEvidenceByAfgorelse,
} from "@/hooks/useAfgorelse";
import type { ReportEvidenceModel } from "@cle/models/reportEvidence";
import {
  AFGOERELSE_LABELS,
  type AfgorelseDetailModel,
  type AfgorelseType,
} from "@/interfaces/afgorelse";
import { formatDanishLongDate } from "@/util/date";
import {
  getEvidenceDocumentPath,
  getEvidenceTypeLabel,
} from "@cle/utils/evidenceDocumentType";
import { toSubmittedInjunctionOverviewRows } from "@cle/utils/calculateInjunctionCategorySummaries";
import InfoModal from "@cle/components/InfoModal";
import AfgorelseInfoContent from "./components/AfgorelseInfoContent";
import InjunctionReportOverview from "./components/InjunctionReportOverview";
import ReportPeriodStatusBadge from "../indberetninger/components/ReportPeriodStatusBadge";
import { formatLessons } from "@cle/utils/lessonLimits";

const MISSING_VALUE = "-";

type SubmittedAfgorelseDetailViewProps = {
  afgorelseType: AfgorelseType;
  detail: AfgorelseDetailModel;
};

type SubmittedAfgorelseDocumentRow = {
  id: string;
  documentType: string;
  fileName: string;
  lessons: number;
  path: string;
  type: string;
};

type ReportedMetricVariant = "default" | "danger" | "success";

const ReportedMetric = ({
  icon,
  label,
  value,
  variant = "default",
}: {
  icon: ReactNode;
  label: string;
  value: string | number;
  variant?: ReportedMetricVariant;
}) => (
  <div
    className={[
      "cle-report-detail__reported-metric",
      variant === "danger"
        ? "cle-report-detail__reported-metric--danger"
        : variant === "success"
          ? "cle-report-detail__reported-metric--success"
          : undefined,
    ]
      .filter(Boolean)
      .join(" ")}
  >
    <span className="cle-report-detail__reported-metric-icon">{icon}</span>
    <span>
      <span className="cle-report-detail__reported-metric-label">{label}</span>
      <span className="cle-report-detail__reported-metric-value">{value}</span>
    </span>
  </div>
);

const isSubmittedAfterDeadline = (
  submittedAt: string | null | undefined,
  deadline: string | null | undefined,
): boolean => {
  if (!submittedAt || !deadline) {
    return false;
  }

  return new Date(submittedAt).getTime() > new Date(deadline).getTime();
};

const mapReportEvidence = (
  evidence: ReportEvidenceModel,
): SubmittedAfgorelseDocumentRow => ({
  id: evidence.id,
  documentType: evidence.evidenceType,
  fileName: evidence.fileName ?? MISSING_VALUE,
  lessons: evidence.numberOfLessons ?? 0,
  path: getEvidenceDocumentPath(evidence.evidenceType, evidence.id) ?? "",
  type: getEvidenceTypeLabel(evidence.evidenceType),
});

const columns: CleTableColumn<SubmittedAfgorelseDocumentRow>[] = [
  {
    id: "fileName",
    header: "Dokument",
    title: (document) => document.fileName,
    cell: (document) => (
      <span className="d-inline-flex align-items-center gap-2">
        <FileText size={15} aria-hidden strokeWidth={1.75} />
        {document.fileName}
      </span>
    ),
  },
  {
    id: "type",
    header: "Type",
    cell: (document) => document.type,
  },
  {
    id: "lessons",
    header: "Lektioner",
    className: "text-end fw-bold",
    cell: (document) => document.lessons,
  },
];

const SubmittedAfgorelseDetailView = ({
  afgorelseType,
  detail,
}: SubmittedAfgorelseDetailViewProps) => {
  const labels = AFGOERELSE_LABELS[afgorelseType];
  const navigate = useNavigate();
  const { mutateAsync: downloadDocument, isPending: isDownloadingDocument } =
    useDownloadAfgorelseDocument(afgorelseType);
  const {
    data: reportEvidence = [],
    isError: isDocumentsError,
    isLoading: isDocumentsLoading,
  } = useReportEvidenceByAfgorelse(afgorelseType, detail.id);

  const submittedAt = formatDanishLongDate(detail.indsendt, MISSING_VALUE);
  const reportingDeadline = formatDanishLongDate(detail.frist, MISSING_VALUE);
  const isSubmittedLate = isSubmittedAfterDeadline(
    detail.indsendt,
    detail.frist,
  );
  const overviewRows = toSubmittedInjunctionOverviewRows(detail);
  const overviewTotals = overviewRows.reduce(
    (totals, row) => ({
      submittedLessons: totals.submittedLessons + row.submittedLessons,
      transferredToNext: totals.transferredToNext + row.transferredToNext,
    }),
    {
      submittedLessons: 0,
      transferredToNext: 0,
    },
  );
  const reportedLessons =
    detail.lektionerIndberettet?.sum ?? overviewTotals.submittedLessons;
  const hasInsufficientLessons = reportedLessons < detail.paakraevedeLektioner;
  const documents = reportEvidence.map(mapReportEvidence);

  const [downloadError, setDownloadError] = useState<string | null>(null);

  const handleDownloadDecision = async () => {
    setDownloadError(null);
    let objectUrl: string | null = null;
    try {
      const blob = await downloadDocument(detail.id);
      objectUrl = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = objectUrl;
      link.download =
        detail.decisionDocumentFileName ?? labels.downloadFallbackFileName;
      link.click();
    } catch (error) {
      setDownloadError(
        error instanceof Error && error.message
          ? error.message
          : "Dokumentet kunne ikke hentes lige nu. Prøv igen.",
      );
    } finally {
      // Revoke on the next tick so the click-triggered download has started.
      if (objectUrl) {
        const urlToRevoke = objectUrl;
        setTimeout(() => URL.revokeObjectURL(urlToRevoke), 0);
      }
    }
  };

  return (
    <div className="cle-report-detail">
      <section className="cle-report-detail__reported-shell">
        <div className="cle-report-detail__reported-hero cle-report-detail__reported-hero-success">
          <div className="cle-report-detail__reported-hero-content">
            <p className="cle-afventende-indberetninger-card__subtitle mb-0">
              {labels.submittedHeroSubtitle}
            </p>
            <div className="d-flex align-items-center gap-2">
              <h1 className="cle-report-detail__reported-title mb-0">
                Afgørelse om efteruddannelse
              </h1>
              <InfoModal
                title="Sådan indberetter du på en afgørelse"
                description="Påbud og pålæg, hvilken dokumentation der kan vælges, kategoriernes maksimum og signering"
                ariaLabel="Læs mere om afgørelser og indberetning"
              >
                <AfgorelseInfoContent />
              </InfoModal>
            </div>

            <p className="cle-report-detail__reported-description">
              {labels.submittedHeroDescription}
            </p>
          </div>
          <ReportPeriodStatusBadge
            className="cle-report-detail__reported-status--completed"
            icon={<CheckCircle2 size={16} aria-hidden strokeWidth={2} />}
            label="Indsendt"
          />
        </div>

        <RoundedCard className="cle-report-detail__reported-summary">
          <div className="cle-report-detail__reported-summary-header mb-0">
            <div>
              <h2 className="cle-report-detail__reported-period mb-0">
                {labels.submittedSummaryTitle}
              </h2>
              <p className="cle-report-detail__reported-description">
                {labels.submittedSummaryDescription}
              </p>
            </div>
            <CleButton
              id={`cle-download-submitted-${afgorelseType}`}
              size="sm"
              variant="outline"
              className="gap-2"
              disabled={isDownloadingDocument}
              onClick={handleDownloadDecision}
            >
              <Download size={16} aria-hidden strokeWidth={1.75} />
              {isDownloadingDocument
                ? "Henter..."
                : `Download ${labels.downloadButtonLabel}`}
            </CleButton>
            {downloadError ? (
              <div className="alert alert-warning mt-2 mb-0" role="alert">
                {downloadError}
              </div>
            ) : null}
          </div>

          <div className="cle-report-detail__reported-metrics">
            <ReportedMetric
              icon={<FileClock size={18} aria-hidden strokeWidth={2} />}
              label="Dokumentationsperiode"
              value={`${formatDanishLongDate(detail.periodeFra)} - ${formatDanishLongDate(detail.periodeTil)}`}
            />
            <ReportedMetric
              icon={<CalendarClock size={18} aria-hidden strokeWidth={2} />}
              label="Indberetningsfrist"
              value={reportingDeadline}
            />
            <ReportedMetric
              icon={<Scale size={18} aria-hidden strokeWidth={2} />}
              label="Indberettede lektioner"
              value={`${formatLessons(reportedLessons)} / ${formatLessons(
                detail.paakraevedeLektioner,
              )}`}
              variant={hasInsufficientLessons ? "danger" : "default"}
            />
            <ReportedMetric
              icon={<ClipboardCheck size={18} aria-hidden strokeWidth={2} />}
              label={isSubmittedLate ? "Indsendt for sent" : "Indsendt"}
              value={submittedAt}
              variant={isSubmittedLate ? "danger" : "success"}
            />
          </div>
        </RoundedCard>

        <InjunctionReportOverview
          rows={overviewRows}
          variant="submitted"
          header={labels.overviewHeader}
          pendingSubmittedColumnLabel={labels.pendingSubmittedColumnLabel}
        />

        <RoundedCard
          bodyClassName="p-0"
          header={labels.submittedDocumentsHeader}
          description={labels.submittedDocumentsDescription}
        >
          <CleTable
            columns={columns}
            data={documents}
            emptyDescription="Når dokumenter er tilgængelige, vises de her."
            emptyTitle="Ingen dokumenter fundet"
            errorMessage="Dokumenterne kunne ikke indlæses lige nu."
            isError={isDocumentsError}
            isLoading={isDocumentsLoading}
            loadingRowCount={Math.max(documents.length, 1)}
            rowAction={{
              onClick: (_documentId, document) => {
                if (document.path) {
                  void navigate(document.path);
                }
              },
            }}
          />
        </RoundedCard>

        <RoundedCard className="cle-report-detail__reported-timeline">
          <div className="cle-report-detail__reported-timeline-item">
            <span className="cle-report-detail__reported-timeline-icon">
              <CheckCircle2 size={18} aria-hidden strokeWidth={2} />
            </span>
            <div>
              <h2 className="cle-report-detail__reported-section-title">
                Dokumentationen er låst til visning
              </h2>
              <p className="text-muted small mb-0">
                {labels.submittedTimelineDescription}
              </p>
            </div>
          </div>
        </RoundedCard>
      </section>
    </div>
  );
};

export default SubmittedAfgorelseDetailView;
